"use client";
import styles from "./Event/event.module.css";
import Avatar from "antd/es/avatar/Avatar";
import dayjs from "dayjs";
import { IconCalendarEvent, IconMapPin, IconBuildingStore } from "@/lib/icons";
import ConvertedTime from "./convertedtime";
import { formatEventTime } from "@/lib/formatDateTime";
import EventTimezone from "./EventTimezone";
import LocationButton from "./LocationButton";
import Container from "./container";
import { Flex, Button, Tag, Grid, Select } from "antd";
import Link from "next/link";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLayout } from "@/lib/LayoutContext";
import { usercontext } from "@/lib/context";
import { setPhoneInputInitialValue } from "@/components/CustomPhoneInput";
import EventSettingsNotice from "./EventSettingsNotice";
import RegistrationLimitMessage from "./RegistrationLimitMessage";
import { useEventEnrollmentCheck } from "@/lib/useEventEnrollmentCheck";
import { useRouter, useSearchParams } from "next/navigation";
import {
  saveThirdPartyGoPayload,
  sourcePlatformLogoUrl,
  thirdPartyGoUrl,
} from "@/lib/thirdPartyRedirect";
import { trackViewItem, trackBookNowClick } from "@/lib/analytics";
import { resolveEventId } from "@/lib/analytics/helpers";
import OrganizerInfoCard from "./Event/OrganizerInfoCard";
import { EXTERNAL_REL, externalRel } from "@/lib/externalLink";

// EventSummary renders twice per event-detail page (once per responsive
// breakpoint, both mounted simultaneously and toggled with CSS). Both copies
// call trackViewItem; lib/analytics dedupes view_item per event, which also
// covers the call made from TrackEventView on the same page.

export function resolveVenueLabel(venue) {
  if (!venue) return null;
  if (typeof venue === "string") return venue.trim() || null;
  if (typeof venue !== "object") return null;

  const name = venue.name?.trim();
  const city = venue.city?.trim();

  if (!name && !city) return null;
  if (name && city && !name.toLowerCase().includes(city.toLowerCase())) {
    return `${name}, ${city}`;
  }
  return name || city;
}

/**
 * A third-party listing can run on several dates/venues (shows). The
 * customer picks one; its date and venue fill the summary and its booking
 * link is used for the redirect.
 */
export default function EventSummary({ shows, ...props }) {
  const upcoming = useMemo(
    () =>
      (Array.isArray(shows) ? shows : []).filter(
        (show) => !show.end_at || dayjs(show.end_at).isAfter(dayjs()),
      ),
    [shows],
  );
  const [showId, setShowId] = useState(null);

  if (!props.is_third_party || upcoming.length < 2) {
    return <EventSummaryBase {...props} />;
  }

  const selected = upcoming.find((show) => show.id === showId) || upcoming[0];
  const picker = (
    <Select
      size="large"
      style={{ width: "100%", marginBottom: 12 }}
      value={selected.id}
      onChange={setShowId}
      aria-label="Choose date and venue"
      options={upcoming.map((show) => ({
        value: show.id,
        label: [
          dayjs(show.start_date).format("ddd, D MMM"),
          show.start_time ? formatEventTime(show.start_time) : null,
          [show.venue?.name, show.venue?.city].filter(Boolean).join(", "),
        ]
          .filter(Boolean)
          .join(" · "),
      }))}
    />
  );

  return (
    <EventSummaryBase
      {...props}
      start_date={selected.start_date}
      start_time={selected.start_time}
      end_date={selected.end_date || selected.start_date}
      end_time={selected.end_time}
      timezone={selected.timezone || props.timezone}
      city={selected.venue?.city || props.city}
      venue={{
        ...(props.venue || {}),
        name: selected.venue?.name,
        line1: selected.venue?.line1,
        city: selected.venue?.city,
        google_place_id: selected.venue?.place_id,
      }}
      yet_to_be_announced={!selected.venue?.name}
      showPicker={picker}
      selectedShowId={selected.id}
    />
  );
}

function EventSummaryBase({
  venue,
  // Booked entry slot of an open-daily show: { date, start_time, end_time }
  slot,
  yet_to_be_announced,
  start_date,
  start_time,
  end_date,
  end_time,
  price,
  city,
  slug,
  status,
  external_link,
  timezone,
  sold_out,
  eventId,
  isUniqueRegistration,
  isTicketTransferable,
  eventName,
  inTicketDetail = false,
  referral_code,
  is_third_party = false,
  require_customer_info_before_redirect = true,
  is_bookable = true,
  source_platform,
  source_platform_logo,
  onEventClick,
  organizer_details,
  // The mobile layout renders the organizer card separately (below the
  // gallery), so its EventSummary copy opts out.
  showOrganizer = true,
  // Multi-show third-party listing: the date/venue picker and its choice.
  showPicker = null,
  selectedShowId = null,
}) {
  const screens = Grid.useBreakpoint();
  // Scale the date/venue row icon+avatar with the viewport — small on
  // narrow phones, back to full size once there's room on desktop.
  const rowAvatarSize = inTicketDetail
    ? screens.lg
      ? 32
      : screens.sm
        ? 28
        : 22
    : 45;
  const rowIconSize = inTicketDetail
    ? screens.lg
      ? 16
      : screens.sm
        ? 14
        : 12
    : 20;
  const footerRef = useRef(null);
  const { setOffset, removeOffset } = useLayout();
  // The sticky booking footer sits above the 60px mobile bottom nav. That
  // offset used to come from LayoutContext, which the nav only fills in from
  // an effect — so the server rendered the footer at bottom: 0 and it jumped
  // 60px after hydration on every mobile event page. It's now CSS (the nav is
  // visible below 768px) plus this one case the CSS can't know: in the app's
  // WebView the site header, and with it the bottom nav, isn't rendered.
  // is_application reads the same on server and client, so no mismatch.
  const inAppWebView = useSearchParams()?.get("is_application") === "true";
  const { user, setIsOpen, setSpecialFn } = useContext(usercontext);
  const router = useRouter();

  const { enrolled: alreadyRegistered, order_id: enrollmentOrderId } =
    useEventEnrollmentCheck(eventId, isUniqueRegistration);
  const showRegistrationLimit =
    alreadyRegistered &&
    isUniqueRegistration &&
    status !== "completed" &&
    !sold_out;

  useEffect(() => {
    const resolvedEventId = resolveEventId(eventId);
    if (!inTicketDetail && resolvedEventId) {
      trackViewItem({
        id: resolvedEventId,
        slug,
        name: eventName,
        price,
        venue,
        start_date,
        timezone,
        address: { city },
      });
    }
  }, [
    eventId,
    inTicketDetail,
    slug,
    eventName,
    price,
    city,
    venue,
    start_date,
    timezone,
  ]);

  useEffect(() => {
    const updateOffset = () => {
      if (footerRef.current && window.innerWidth <= 991) {
        setOffset("event_summary_footer", footerRef.current.offsetHeight);
      } else {
        removeOffset("event_summary_footer");
      }
    };

    updateOffset();
    window.addEventListener("resize", updateOffset);

    let resizeObserver;
    if (footerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateOffset);
      resizeObserver.observe(footerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateOffset);
      resizeObserver?.disconnect();
      removeOffset("event_summary_footer");
    };
  }, [setOffset, removeOffset, showRegistrationLimit]);

  const goToBookingRedirect = (contact = {}) => {
    saveThirdPartyGoPayload({
      eventId,
      showId: selectedShowId,
      slug,
      eventName,
      sourcePlatform: source_platform,
      sourcePlatformLogo: sourcePlatformLogoUrl(source_platform_logo),
      city,
      name: contact.name,
      mobile: contact.mobile,
      country_code: contact.country_code,
      phone: contact.phone,
    });
    router.push(thirdPartyGoUrl(eventId));
  };

  // The account's details are what the booking site receives, so a guest
  // logs in first (the same popup booking uses) instead of typing a name and
  // mobile. The login form hands back first_name/last_name/phone.
  const contactFromUser = (account) => {
    const parsedPhone = account?.phone
      ? setPhoneInputInitialValue(account.phone)
      : {};
    return {
      name:
        account?.name ||
        [account?.first_name, account?.last_name].filter(Boolean).join(" ") ||
        "",
      mobile: account?.phone
        ? account.phone.startsWith("+")
          ? account.phone
          : `+${account.phone}`
        : "",
      country_code: parsedPhone.country_code || account?.country_code || "IN",
      phone: parsedPhone.phone || account?.phone || "",
    };
  };

  return (
    <div
      className={`${styles.eventSummarySticky} ${inTicketDetail ? "w-full !static !top-auto" : ""}`}
    >
      {!inTicketDetail && !is_third_party && (
        <EventSettingsNotice isTicketTransferable={isTicketTransferable} />
      )}
      <div
        className={`${styles.eventSummary} ${inTicketDetail ? "flex flex-col justify-start !mb-0" : ""}`}
      >
        {inTicketDetail && (
          <div
            className={`${styles.eventSummaryItem} !py-1.5 !px-2.5 sm:!py-2.5 sm:!px-3.5`}
            style={{
              fontFamily: "Bricolage Grotesque",
              fontWeight: 600,
              fontSize: screens.lg ? "17px" : screens.sm ? "13px" : "12px",
              lineHeight: "1.2",
              minHeight: screens.lg ? "26px" : "18px",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              wordBreak: "break-word",
              cursor: onEventClick ? "pointer" : "default",
            }}
            onClick={onEventClick}
          >
            <span className={onEventClick ? "hover:underline" : ""}>
              {eventName}
            </span>
          </div>
        )}
        <div
          className={`${styles.eventSummaryItem} ${inTicketDetail ? "!py-1.5 !px-2.5 sm:!py-2.5 sm:!px-3.5 gap-1.5" : ""}`}
        >
          <Avatar
            size={rowAvatarSize}
            style={
              inTicketDetail
                ? {
                    width: rowAvatarSize,
                    height: rowAvatarSize,
                    minWidth: rowAvatarSize,
                    flexShrink: 0,
                    borderRadius: "50%",
                  }
                : undefined
            }
            icon={<IconCalendarEvent size={rowIconSize} />}
          />
          <span
            className={
              inTicketDetail
                ? "text-[11px] sm:text-[13px] min-[992px]:text-[15px] leading-snug"
                : undefined
            }
          >
            {slot ? (
              <>
                <strong>Date: </strong>
                {dayjs(slot.date).format("ddd, DD MMM YYYY")}
              </>
            ) : (
              <>
                <strong>Starts at: </strong>
                <ConvertedTime
                  time={start_time}
                  date={start_date}
                  timezone={timezone}
                />
              </>
            )}
          </span>
        </div>
        {end_date && (
          <div
            className={`${styles.eventSummaryItem} ${inTicketDetail ? "!py-1.5 !px-2.5 sm:!py-2.5 sm:!px-3.5 gap-1.5" : ""}`}
          >
            <Avatar
              size={rowAvatarSize}
              style={
                inTicketDetail
                  ? {
                      width: rowAvatarSize,
                      height: rowAvatarSize,
                      minWidth: rowAvatarSize,
                      flexShrink: 0,
                      borderRadius: "50%",
                    }
                  : undefined
              }
              icon={<IconCalendarEvent size={rowIconSize} />}
            />
            <span
              className={
                inTicketDetail
                  ? "text-[11px] sm:text-[13px] min-[992px]:text-[15px] leading-snug"
                  : undefined
              }
            >
              {slot ? (
                <>
                  <strong>Time slot: </strong>
                  {formatEventTime(slot.start_time)} –{" "}
                  {formatEventTime(slot.end_time)}
                </>
              ) : (
                <>
                  <strong>Ends at: </strong>
                  <ConvertedTime
                    time={end_time}
                    date={end_date}
                    timezone={timezone}
                  />
                </>
              )}
            </span>
          </div>
        )}

        {timezone && <EventTimezone timezone={timezone} />}
        {venue?.is_public ? (
          <a
            href={`/venue/${venue?.google_place_id}`}
            target="_blank"
            className="w-full block"
          >
            <div
              className={`${styles.eventSummaryItem} ${inTicketDetail ? "!py-1.5 !px-2.5 sm:!py-2.5 sm:!px-3.5 gap-1.5" : ""}`}
            >
              <Avatar
                size={rowAvatarSize}
                style={
                  inTicketDetail
                    ? {
                        width: rowAvatarSize,
                        height: rowAvatarSize,
                        minWidth: rowAvatarSize,
                        flexShrink: 0,
                        borderRadius: "50%",
                      }
                    : undefined
                }
                icon={<IconMapPin size={rowIconSize} />}
              />
              <span
                className={
                  inTicketDetail
                    ? "text-[11px] sm:text-[13px] min-[992px]:text-[15px] leading-snug"
                    : undefined
                }
              >
                {yet_to_be_announced
                  ? `Venue to be announced${venue?.city ? `, ${venue.city}` : ""}`
                  : resolveVenueLabel(venue) || "Venue to be announced"}
              </span>
              <LocationButton
                place_id={venue?.google_place_id}
                venue_name={resolveVenueLabel(venue)}
                is_public={venue?.is_public}
                inDetail={inTicketDetail}
              />
            </div>
          </a>
        ) : resolveVenueLabel(venue) ? (
          <a
            href={
              venue?.google_place_id
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(resolveVenueLabel(venue) || "Venue")}&query_place_id=${venue.google_place_id}`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(resolveVenueLabel(venue))}`
            }
            target="_blank"
            rel={EXTERNAL_REL}
            className="w-full block"
          >
            <div
              className={`${styles.eventSummaryItem} ${inTicketDetail ? "!py-1.5 !px-2.5 sm:!py-2.5 sm:!px-3.5 gap-1.5" : ""}`}
            >
              <Avatar
                size={rowAvatarSize}
                style={
                  inTicketDetail
                    ? {
                        width: rowAvatarSize,
                        height: rowAvatarSize,
                        minWidth: rowAvatarSize,
                        flexShrink: 0,
                        borderRadius: "50%",
                      }
                    : undefined
                }
                icon={<IconMapPin size={rowIconSize} />}
              />
              <span
                className={
                  inTicketDetail
                    ? "text-[11px] sm:text-[13px] min-[992px]:text-[15px] leading-snug"
                    : undefined
                }
              >
                {yet_to_be_announced
                  ? `Venue to be announced${venue?.city ? `, ${venue.city}` : ""}`
                  : resolveVenueLabel(venue) || "Venue to be announced"}
              </span>
              <LocationButton
                place_id={venue?.google_place_id}
                venue_name={resolveVenueLabel(venue)}
                is_public={venue?.is_public}
                inDetail={inTicketDetail}
              />
            </div>
          </a>
        ) : null}
      </div>
      {!inTicketDetail && (
        <div
          className={`${styles.eventSummaryFooter} ${inAppWebView ? styles.eventSummaryFooterNoBottomNav : ""}`}
          ref={footerRef}
        >
          <Container>
            {showRegistrationLimit ? (
              <RegistrationLimitMessage order_id={enrollmentOrderId} />
            ) : (
              <Flex justify="space-between" align="center">
                <Flex vertical gap={2}>
                  <div className={styles.eventPricing}>
                    <strong>{price}</strong>
                    <span>onwards</span>
                  </div>
                  {status === "completed" || sold_out ? null : (
                    <div className={styles.eventStock}>
                      Tickets? Still in Stock!
                    </div>
                  )}
                  {isUniqueRegistration &&
                    status !== "completed" &&
                    !sold_out && (
                      <div className="text-xs text-[#57534E]">
                        One booking per mobile number
                      </div>
                    )}
                </Flex>
                {status === "completed" ? (
                  <Tag
                    color="blue"
                    className="!px-[24px] !py-[8px]"
                    style={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: "#91caff",
                    }}
                  >
                    Completed
                  </Tag>
                ) : sold_out ? (
                  <Tag
                    color="red"
                    className="!px-[24px] !py-[8px]"
                    style={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: "#ffccc7",
                    }}
                  >
                    Sold Out
                  </Tag>
                ) : is_third_party && !is_bookable ? (
                  <Tag
                    color="default"
                    className="!px-[24px] !py-[8px]"
                    style={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: "#d9d9d9",
                    }}
                  >
                    Not Available
                  </Tag>
                ) : is_third_party ? (
                  <>
                  {showPicker}
                  <Button
                    size="large"
                    type="primary"
                    className={styles.bookNow}
                    onClick={() => {
                      if (user?.id) {
                        goToBookingRedirect(contactFromUser(user));
                        return;
                      }
                      if (require_customer_info_before_redirect) {
                        setIsOpen(true);
                        setSpecialFn({
                          fn: (loggedIn) =>
                            goToBookingRedirect(contactFromUser(loggedIn?.[0])),
                        });
                        return;
                      }
                      goToBookingRedirect();
                    }}
                  >
                    Find Tickets
                  </Button>
                  </>
                ) : (
                  <Link
                    rel={externalRel(external_link)}
                    href={
                      external_link ||
                      (referral_code?.referral_code
                        ? `/${slug}/book?referral_code=${encodeURIComponent(referral_code.referral_code)}`
                        : `/${slug}/book`)
                    }
                    prefetch
                  >
                    <Button
                      size="large"
                      type="primary"
                      className={styles.bookNow}
                      onClick={() => {
                        trackBookNowClick({
                          event_id: resolveEventId(eventId),
                          event_name: eventName,
                          event_city: city,
                          event_date: start_date,
                        });
                      }}
                    >
                      Book Now
                    </Button>
                  </Link>
                )}
              </Flex>
            )}
          </Container>
        </div>
      )}
      {showOrganizer && organizer_details?.workspace_slug && (
        <div className="w-full mt-4">
          <OrganizerInfoCard
            organizerSlug={organizer_details.workspace_slug}
            initialData={organizer_details}
          />
        </div>
      )}
    </div>
  );
}
